'use client'

import { ReactNode } from 'react'
import { Cell, Spacer, Text } from '@vinted/web-ui'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'

import type { Slide, ABTestConfig } from '../newContentTestConfig'
import { BulletPointList } from '../BulletPointList'
import { ConversationSlider } from '../ConversationSlider'
import { ReportImagesSlider } from '../ReportImagesSlider'

type Props = {
  slide: Slide
  slideIndex: number
  currentConversationIndex: number
  isEducationStarted: boolean
  config: ABTestConfig
  isManualMode: boolean
  onConversationNavigate: (direction: 'prev' | 'next') => void
  onConversationComplete?: () => void
  helpCentreTranslationValues: Record<string, (chunks: Array<ReactNode>) => ReactNode>
}

export const EducationSlide = ({
  slide,
  slideIndex,
  currentConversationIndex,
  isEducationStarted,
  config,
  isManualMode,
  onConversationNavigate,
  onConversationComplete,
  helpCentreTranslationValues,
}: Props) => {
  const translate = useTranslate('conversation.phishing_first_time_lister_education')
  const hasReportSteps = slide.step1 || slide.step2 || slide.step3
  const hasConversations = slide.conversations?.length

  return (
    <div className="phishing-first-time-lister-education__slide-content">
      <Cell>
        <Text
          as="h1"
          text={translate(slide.title)}
          type={config.titleType}
          alignment={Text.Alignment.Left}
          width={Text.Width.Parent}
        />
        <Spacer size={Spacer.Size.Large} />
        <Text
          as="span"
          text={
            <div className="u-force-wrap">{translate(slide.body, helpCentreTranslationValues)}</div>
          }
          type={Text.Type.Body}
          alignment={Text.Alignment.Left}
          html
        />

        {hasReportSteps && slide.helpPrompt && (
          <>
            <Spacer size={Spacer.Size.Larger} />
            <Spacer size={Spacer.Size.XSmall} />
            <div className="u-force-wrap">
              {translate(slide.helpPrompt, helpCentreTranslationValues)}
            </div>
            {slide.sectionTitle && (
              <>
                <Spacer size={Spacer.Size.X2Large} />
                <Text
                  as="h3"
                  text={translate(slide.sectionTitle)}
                  type={Text.Type.Title}
                  alignment={Text.Alignment.Left}
                  width={Text.Width.Parent}
                />
              </>
            )}
          </>
        )}

        {!hasReportSteps && slide.sectionTitle && (
          <>
            <Spacer size={Spacer.Size.X2Large} />
            <Text
              as="h3"
              text={translate(slide.sectionTitle)}
              type={Text.Type.Title}
              alignment={Text.Alignment.Left}
              width={Text.Width.Parent}
            />
          </>
        )}
        {slide.bulletPoints && <BulletPointList bulletPoints={slide.bulletPoints} />}
      </Cell>

      <div className="phishing-first-time-lister-education__expandable-section">
        {hasConversations && slide.conversations && (
          <ConversationSlider
            conversations={slide.conversations}
            currentConversationIndex={currentConversationIndex}
            isEducationStarted={isEducationStarted}
            config={config}
            currentSlideIndex={slideIndex}
            isManualMode={isManualMode}
            onNavigate={onConversationNavigate}
            onComplete={onConversationComplete}
          />
        )}
        {hasReportSteps && (
          <ReportImagesSlider
            reportImages={slide.reportImages || ['report-1.png', 'report-2.png']}
            currentConversationIndex={currentConversationIndex}
            isEducationStarted={isEducationStarted}
            currentSlideIndex={slideIndex}
            isManualMode={isManualMode}
            config={config}
            onNavigate={onConversationNavigate}
            onComplete={onConversationComplete}
          />
        )}
      </div>
    </div>
  )
}
